import httpWrapper from '../HttpWrapper'
import { AdminGetRes } from './models/adminGet/AdminGetRes'
import { CreateTenantReq } from './models/createTenant/CreateTenantReq'
import { CreateTenantRes } from './models/createTenant/CreateTenantRes'
import { GetAllRes } from './models/getAll/GetAllRes'
import { GetLoggedInUserRes } from './models/getLoggedInUser/GetLoggedInUserRes'
import { CreateUserReq } from './models/createUser/CreateUserReq'
import { CreateUserRes } from './models/createUser/CreateUserRes'
import { UpdateUserReq } from './models/updateUser/UpdateUserReq'
import { UserDto } from './models/UserDto'
import { UpdateUserSettingsReq } from './models/updateUserSettings/UpdateUserSettingsReq'
import { GetAvailableSeeds } from './models/getAvailableSeeds/GetAvailableSeedsRes'
import { GetAvailableSeedPersonIdsByDepartmentsReq } from './models/getAvailableSeedPersonIdsByDepartments/GetAvailableSeedPersonIdsByDepartmentsReq'
import { GetAvailableSeedPersonIdsByDepartmentsRes } from './models/getAvailableSeedPersonIdsByDepartments/GetAvailableSeedPersonIdsByDepartmentsRes'
import { GetTenantUsersRes } from './models/getTenantUsers/GetTenantUsersRes'
import { LookupUserByEmailReq } from './models/lookupUser/LookupUserByEmailReq'
import { LookupUserByEmailRes } from './models/lookupUser/LookupUserByEmailRes'
import { BackfillReferralCodesRes } from './models/BackfillReferralCodesRes'
import { GetReferralStatsRes } from './models/GetReferralStatsRes'
import { ListUsersReq } from './models/listUsers/ListUsersReq'
import { ListUsersRes } from './models/listUsers/ListUsersRes'

class TenantApi {
    private static instance: TenantApi

    public constructor() {
        if (TenantApi.instance) {
            return TenantApi.instance
        }

        TenantApi.instance = this
    }

    public async getAll(params?: {
        includePersonal?: boolean
    }): Promise<GetAllRes> {
        const queryParams = new URLSearchParams()
        if (params?.includePersonal !== undefined) {
            queryParams.set('includePersonal', String(params.includePersonal))
        }
        return await httpWrapper.get('/tenant/all', queryParams)
    }

    public async getLoggedInUser(): Promise<GetLoggedInUserRes> {
        return await httpWrapper.get(`/tenant/get-logged-in-user`)
    }

    public async createTenant(
        createTenantReq: CreateTenantReq,
    ): Promise<CreateTenantRes> {
        return await httpWrapper.post('/tenant/create', createTenantReq)
    }

    public async adminGet(tenantId: string): Promise<AdminGetRes> {
        const queryParams = new URLSearchParams({
            tenantId,
        })

        return await httpWrapper.get('/tenant/admin-get', queryParams)
    }

    public async getUser(userId: string): Promise<{ user: UserDto }> {
        const queryParams = new URLSearchParams({
            userId,
        })

        return await httpWrapper.get('/tenant/get-user', queryParams)
    }

    public async createUser(
        createUserReq: CreateUserReq,
    ): Promise<CreateUserRes> {
        return await httpWrapper.post('/tenant/create-user', createUserReq)
    }

    public async updateUser(updateUserReq: UpdateUserReq): Promise<void> {
        await httpWrapper.post('/tenant/update-user', updateUserReq)
    }

    public async updateUserSettings(
        updateUserSettingsReq: UpdateUserSettingsReq,
    ): Promise<void> {
        await httpWrapper.post(
            '/tenant/update-user-settings',
            updateUserSettingsReq,
        )
    }

    public async setUserPassword(params: {
        userId: string
        password: string
    }): Promise<void> {
        await httpWrapper.post('/tenant/set-user-password', params)
    }

    public async addNetworkSeedPerson(
        personId: string,
        tenantId: string,
    ): Promise<void> {
        await httpWrapper.post('/tenant/add-network-seed-person', {
            personId,
            tenantId,
        })
    }

    public async removeNetworkSeedPerson(
        personId: string,
        tenantId: string,
    ): Promise<void> {
        await httpWrapper.post('/tenant/remove-network-seed-person', {
            personId,
            tenantId,
        })
    }

    public async linkTenantToCompanies(
        companyIds: string[],
        tenantId: string,
    ): Promise<void> {
        await httpWrapper.post('/tenant/link-tenant-to-companies', {
            companyIds,
            tenantId,
        })
    }

    public async getAvailableSeeds(): Promise<GetAvailableSeeds> {
        return await httpWrapper.get('/tenant/get-available-seed-ids')
    }

    public async getAvailableSeedPersonIdsByDepartments(
        req: GetAvailableSeedPersonIdsByDepartmentsReq,
    ): Promise<GetAvailableSeedPersonIdsByDepartmentsRes> {
        return await httpWrapper.post(
            '/tenant/get-available-seed-person-ids-by-departments',
            req,
        )
    }

    public async unlinkCompanyFromTenant(
        companyId: string,
        tenantId: string,
    ): Promise<void> {
        await httpWrapper.post('/tenant/unlink-company-from-tenant', {
            companyId,
            tenantId,
        })
    }

    public async getTenantUsers(tenantId: string): Promise<GetTenantUsersRes> {
        return await httpWrapper.post('/tenant/get-tenant-users', {
            tenantId,
        })
    }

    public async getAllUsers(params: ListUsersReq): Promise<ListUsersRes> {
        return await httpWrapper.post('/tenant/get-all-users', params)
    }

    public async lookupUserByEmail(
        req: LookupUserByEmailReq,
    ): Promise<LookupUserByEmailRes> {
        return await httpWrapper.post('/tenant/lookup-user-by-email', req)
    }

    public async backfillReferralCodes(): Promise<BackfillReferralCodesRes> {
        return await httpWrapper.post('/tenant/backfill-referral-codes')
    }

    public async getReferralStats(params: {
        start: string
        end: string
    }): Promise<GetReferralStatsRes> {
        const queryParams = new URLSearchParams({
            start: params.start,
            end: params.end,
        })

        return await httpWrapper.get('/tenant/referral-stats', queryParams)
    }
}

const tenantApi = new TenantApi()
export default tenantApi
